/* eslint-disable no-plusplus */
import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  loadModules,
  setDefaultOptions,
  loadCss,
  loadScript,
} from 'esri-loader';
import { createEmptyAxiosInstance } from 'utils/api';
import { logger } from 'utils/debug';
import styled from 'styled-components';
import { useSelector } from 'react-redux';
import {
  Button,
  Typography,
  Col,
  Row,
  Space,
  Result,
  Form,
  Input,
} from 'antd';
import { CameraOutlined, LinkOutlined, ReloadOutlined } from '@ant-design/icons';
import NoPermission from './NoPermission';

const { Title, Text, Link } = Typography;

const HTTP = createEmptyAxiosInstance();

setDefaultOptions({ css: true });
loadCss('4.25');
loadScript({ version: '4.25' });

const coloniasConfig = {
  url: 'https://services7.arcgis.com/Xmm2kRiMsztfCefg/arcgis/rest/services/Cd_Juarez/FeatureServer/1',
  title: 'Colonias',
};

const municipiosConfig = {
  url: 'https://services7.arcgis.com/Xmm2kRiMsztfCefg/arcgis/rest/services/Cd_Juarez/FeatureServer/3',
  title: 'Municipios',
};

const prediosRender = {
  type: 'simple',
  symbol: {
    type: 'simple-fill',
    color: [0, 0, 255, 0.1],
    outline: {
      width: 0.5,
      color: 'white',
    },
  },
};

const hexToRgb = (hex) => {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})?$/i.exec(hex);
  return result ? result.splice(1).map((e) => parseInt(e, 16)).filter((e) => e) : null;
};

const prediosConfig = {
  url: 'https://services7.arcgis.com/Xmm2kRiMsztfCefg/arcgis/rest/services/pract/FeatureServer/0',
  title: 'Predios',
  renderer: prediosRender,
  visible: true,
};

const generateQuery = (
  clave_catastral,
  format = 'geojson',
) => {
  if (clave_catastral) {
    return `${prediosConfig.url}/query?where=(CLAVECATA%20LIKE%20%27${clave_catastral}%25%27)&f=${format}`;
  }
  return `${prediosConfig.url}/query?f=${format}`;
};

const getModules = async () => {
  try {
    const modules = await loadModules([
      'esri/views/MapView',
      'esri/Map',
      'esri/layers/FeatureLayer',
      'esri/widgets/LayerList',
      'esri/layers/GraphicsLayer',
      'esri/Graphic',
      'esri/widgets/Fullscreen',
    ]);
    return [null, modules];
  } catch {
    return ['Ha ocurrido un error al cargar los módulos', null];
  }
};

export const validateGIS = async (_clave_catastral) => {
  try {
    const idxOfHypen = _clave_catastral.indexOf('-');
    const placesToRemove = idxOfHypen !== -1 ? idxOfHypen + 1 : 0;
    const clave_catastral = _clave_catastral.substring(placesToRemove).replace(/-/g, '');

    const response = await HTTP.get(`${generateQuery(clave_catastral)}&returnUniqueIdsOnly=true`);
    if (response.status === 200 && response.data.uniqueIds?.length >= 1) {
      return [true, response.data.uniqueIds[0]];
    }
  } catch (error) {
    logger(error);
  }
  return [false];
};

const getAttributes = async (uniqueIds) => {
  try {
    const params = '&outFields=*&returnGeometry=false';
    const response = await HTTP
      .get(generateQuery(null, 'json').concat(params).concat(`&objectIds=${uniqueIds}`));
    if (response.status === 200) {
      const attributes = response.data?.features[0]?.attributes;
      if (attributes) {
        return attributes;
      }
    }
  } catch (error) {
    logger('error', error);
  }
  return null;
};

const getRings = async (clave_catastral, _clave_catastral) => {
  try {
    const [isValid, uniqueIds] = await validateGIS(clave_catastral);
    if (isValid) {
      const response = await HTTP.get(generateQuery(clave_catastral));
      if (response.status === 200) {
        const rings = response.data.features?.[0]?.geometry?.coordinates;
        const attributes = await getAttributes(uniqueIds);
        if (rings && attributes) {
          return [null, rings, attributes];
        }
      }
    }
  } catch (error) {
    logger('error', error);
  }
  return [`No fue posible encontrar las coordenadas para clave catastral "${_clave_catastral}"`];
};

const isWhiteImage = (cadenaBase64, umbralBlanco = 255) => {
  const datosDecodificados = atob(cadenaBase64);
  const arrayBytes = new Uint8Array(datosDecodificados.length);
  for (let i = 0; i < datosDecodificados.length; i++) {
    arrayBytes[i] = datosDecodificados.charCodeAt(i);
  }

  let conteoBlanco = 0;

  for (let i = 0; i < arrayBytes.length; i++) {
    if (arrayBytes[i] >= umbralBlanco) {
      conteoBlanco++;
    }
  }

  return conteoBlanco === 0;
};

const ArcGisMap = ({
  clave_catastral_municipal: _clave_catastral,
  setLoading,
  callbackAttrs,
  hasPermission,
  disabled: _disabled,
  screenshot,
  estados_globales,
  required,
  attrs,
  autoCapture,
}) => {
  const disabled = (_disabled && screenshot) || estados_globales === 5;
  const form = Form.useFormInstance();
  const base64 = Form.useWatch('base64', form);
  const theme = useSelector(({ app }) => app.theme);
  const [error, setError] = useState();
  const [attributes, setAttributes] = useState();
  const takeScreenshotButtonRef = useRef();

  const idxOfHypen = _clave_catastral.indexOf('-');
  const placesToRemove = idxOfHypen !== -1 ? idxOfHypen + 1 : 0;
  const clave_catastral = _clave_catastral.substring(placesToRemove).replace(/-/g, '');

  const loadMap = async () => {
    const [modulesError, modules] = await getModules();
    if (modulesError) {
      throw new Error(modulesError);
    }
    const [
      MapView,
      Map,
      FeatureLayer,
      LayerList,
      GraphicsLayer,
      Graphic,
      Fullscreen,
    ] = modules;
    const [ringsError, rings, _attributes] = await getRings(clave_catastral, _clave_catastral);
    if (ringsError) {
      throw new Error(ringsError);
    }
    if (!attrs) {
      callbackAttrs({
        usosuelo: _attributes.Nombre_USO,
        claveus: _attributes.Clave_USO,
        descripcionus: _attributes.descripcio,
      });
    }
    setAttributes(_attributes);
    if (screenshot) {
      form.setFieldsValue({ base64: screenshot });
      return;
    }
    if (disabled) return;
    const map = new Map({ basemap: 'hybrid' });
    const lats = rings[0].map((e) => e[0]);
    const longs = rings[0].map((e) => e[1]);
    const latMax = Math.max(...lats);
    const latMin = Math.min(...lats);
    const latDiff = latMax - latMin;
    const longMax = Math.max(...longs);
    const longMin = Math.min(...longs);
    const longDiff = longMax - longMin;
    const lat = latMax - (latDiff / 2);
    const long = longMax - (longDiff / 2);
    const view = new MapView({
      container: 'map-container',
      map,
      center: [lat, long],
      zoom: 19,
      constraints: {
        minZoom: 16,
        maxZoom: 21,
      },
    });

    const stopPropagation = (e) => e.stopPropagation();
    // Preventing drag
    view.on('drag', stopPropagation);
    view.on('drag', ['Shift'], stopPropagation);
    view.on('drag', ['Shift', 'Control'], stopPropagation);

    // Preventing zoom
    view.on('mouse-wheel', stopPropagation);
    view.on('double-click', stopPropagation);
    view.on('double-click', ['Control'], stopPropagation);
    view.on('key-down', (event) => {
      const prohibitedKeys = ['+', '-', 'Shift', '_', '='];
      const keyPressed = event.key;
      if (prohibitedKeys.indexOf(keyPressed) !== -1) {
        event.stopPropagation();
      }
    });

    // Layers
    const municipios = new FeatureLayer(municipiosConfig);
    const colonias = new FeatureLayer(coloniasConfig);
    const predios = new FeatureLayer(prediosConfig);
    await municipios.load();
    await colonias.load();
    await predios.load();

    map.addMany([municipios, colonias, predios]);

    const listaCapas = new LayerList({ view });
    const fullscreen = new Fullscreen({ view });
    view.ui.add(listaCapas, 'top-right');
    view.ui.add(fullscreen, 'top-left');

    // Polygon para predio de clave catastral brindada
    const graphicsLayer = new GraphicsLayer();
    map.add(graphicsLayer);
    const polygon = { type: 'polygon', rings };

    const color = hexToRgb(theme.activeBackgroundColor);
    const simpleFillSymbol = {
      type: 'simple-fill',
      color: color.length !== 3 ? color.concat(100) : color,
      outline: {
        color: [255, 255, 255],
        width: 1,
      },
    };

    const polygonGraphic = new Graphic({
      geometry: polygon,
      symbol: simpleFillSymbol,
    });

    const polygonGraphicSS = new Graphic({
      geometry: polygon,
      symbol: {
        ...simpleFillSymbol,
        color: [120, 120, 120],
      },
    });

    graphicsLayer.add(polygonGraphic);

    const onClick = async () => {
      if (form.getFieldValue('base64')) {
        form.setFieldsValue({ base64: null });
        return;
      }
      setLoading(true);
      try {
        const options = { ignoreBackground: true, quality: 100, format: 'png' };
        const ss = await view.takeScreenshot(options);
        const isWhite = isWhiteImage(ss?.dataUrl.replace('data:image/png;base64,', ''));
        if (ss?.dataUrl && !isWhite) {
          callbackAttrs({
            usosuelo: _attributes.Nombre_USO,
            claveus: _attributes.Clave_USO,
            descripcionus: _attributes.descripcio,
          });
          setTimeout(() => form.setFieldsValue({ base64: ss.dataUrl }));
        }
      } catch (err) {
        logger('[err]', err);
      }
      setTimeout(() => {
        graphicsLayer.remove(polygonGraphicSS);
        graphicsLayer.add(polygonGraphic);
        map.basemap = 'hybrid';
      }, 2000);
      setLoading(false);
    };

    const handleClick = () => {
      map.basemap = 'gray-vector';
      graphicsLayer.remove(polygonGraphic);
      graphicsLayer.add(polygonGraphicSS);
      setTimeout(onClick, 2000);
    };
    if (hasPermission && !autoCapture) {
      takeScreenshotButtonRef.current.addEventListener('click', handleClick);
    }
    view.when(() => {
      if (autoCapture) {
        setTimeout(handleClick, 5000);
      }
      setError();
      setLoading(false);
      setAttributes(_attributes);
    });
  };

  useEffect(() => {
    const load = async () => {
      try {
        setLoading(true);
        if (!clave_catastral) {
          throw new Error('Clave catastral no brindada');
        }
        await loadMap();
      } catch (err) {
        setError(err.message);
      }
      setLoading(false);
    };
    load();
  }, [clave_catastral]);

  if (!hasPermission) {
    return (
      <>
        <NoPermission />
        <MapDiv style={{ position: 'absolute', zIndex: -1 }} />
      </>
    );
  }

  if (error) {
    return (
      <Row justify="center" style={{ width: '100%' }}>
        <Result
          status="warning"
          title="No fue posible cargar la información"
          extra={error}
        />
      </Row>
    );
  }

  return (
    <>
      <Row style={{ width: '100%', marginTop: 15 }} gutter={[10, 10]}>
        {!!attributes && (
          <>
            <Col span={24}>
              <Title level={3}>
                Información del predio:
              </Title>
            </Col>
            <Col span={8}>
              <Space>
                <Text>
                  CLAVE CATASTRAL:
                </Text>
                <Text>
                  {attributes.CLAVECATA}
                </Text>
              </Space>
            </Col>
            <Col span={8}>
              <Space>
                <Text>
                  USO DE SUELO:
                </Text>
                <Text>
                  {attributes.Nombre_USO}
                </Text>
              </Space>
            </Col>
            <Col span={8} />
            <Col span={8}>
              <Space>
                <Text>
                  CLAVE USO DE SUELO:
                </Text>
                <Text>
                  {attributes.Clave_USO}
                </Text>
              </Space>
            </Col>
            <Col span={8}>
              <Space>
                <Text>
                  LIGA PDF USO:
                </Text>
                {attributes.url_uso ? (
                  <Link href={attributes.url_uso} target="_blank">
                    Visitar
                    <LinkOutlined />
                  </Link>
                ) : (
                  <Text>
                    Sin liga
                  </Text>
                )}
              </Space>
            </Col>
            <Col span={24}>
              <Space>
                <Text>
                  DESCRIPCIÓN USO DE SUELO:
                </Text>
                <Text>
                  {attributes.descripcio}
                </Text>
              </Space>
            </Col>
          </>
        )}
        {!disabled && !autoCapture && (
          <Col span={8} offset={16}>
            <Button
              ref={takeScreenshotButtonRef}
              style={{ marginBottom: 15, float: attributes ? 'right' : 'left' }}
              disabled={disabled}
            >
              {!base64 ? 'Tomar captura' : 'Volver a capturar'}
              {!base64 ? <CameraOutlined /> : <ReloadOutlined />}
            </Button>
          </Col>
        )}
      </Row>
      {!!base64 && (<img src={base64} style={{ width: '100%' }} alt="GIS screenshot" />)}
      <MapDiv style={{ display: base64 ? 'none' : null }} />
      <Form.Item name="base64" rules={[{ required }]} hidden><Input /></Form.Item>
    </>
  );
};

const MapDiv = styled.div.attrs({ id: 'map-container' })`
  height: calc((100vw - 100px) / 2);
  width: calc(100vw - 100px);
  margin-top: 30px;
  .esri-layer-list__item:first-of-type {
    display: none;
  }
`;

ArcGisMap.propTypes = {
  clave_catastral_municipal: PropTypes.string,
  setLoading: PropTypes.func,
  callbackAttrs: PropTypes.func,
  hasPermission: PropTypes.bool,
  screenshot: PropTypes.string,
  disabled: PropTypes.bool,
  estados_globales: PropTypes.number,
  required: PropTypes.bool,
  attrs: PropTypes.bool,
  autoCapture: PropTypes.bool,
};

ArcGisMap.defaultProps = {
  clave_catastral_municipal: '',
  setLoading: () => {},
  callbackAttrs: () => { },
  hasPermission: false,
  screenshot: null,
  disabled: false,
  estados_globales: 2,
  required: true,
  attrs: true,
  autoCapture: false,
};

export default ArcGisMap;
